// services/analyticsService.js
// Lightweight client-side analytics tracking — never throws, never blocks
import api from '../utils/api';

const SESSION_KEY = 'resto360_analytics_sid';   // per browser session (sessionStorage)
const VISITOR_KEY = 'resto360_analytics_vid';   // persistent across sessions (localStorage)

function newId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function getSessionId() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = newId();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return null;
  }
}

// Persistent visitor ID — survives across browser sessions so we can
// distinguish NEW vs RETURNING visitors.
function getVisitorId() {
  try {
    let vid = localStorage.getItem(VISITOR_KEY);
    if (!vid) {
      vid = newId();
      localStorage.setItem(VISITOR_KEY, vid);
    }
    return vid;
  } catch {
    return null;
  }
}

function getApiBase() {
  try {
    // Reuse the main API resolver (env var, localStorage override, HTTPS-aware
    // auto-detection, production fallback) so analytics never diverges from the
    // app's real backend — and never triggers mixed-content blocks over HTTPS.
    return api.getApiUrl();
  } catch {
    return '/api';
  }
}

/**
 * Normalize a pathname so dynamic IDs collapse into a single bucket.
 * e.g. /restaurants/8f3.../analytics -> /restaurants/:id/analytics
 * Keeps the analytics "Top Pages" list meaningful instead of one row per ID.
 */
export function normalizePath(pathname) {
  if (!pathname) return '/';
  return pathname
    // UUIDs
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:id')
    // Mongo-style / long hex ids
    .replace(/\/[0-9a-f]{16,}/gi, '/:id')
    // Pure numeric segments
    .replace(/\/\d+/g, '/:id')
    .replace(/\/$/, '') || '/';
}

/**
 * Track a page view.
 * @param {string} page  e.g. '/', '/signin'
 * @param {object} meta  optional: { isLanding: true, referrer: '...' }
 */
export function trackPageView(page, meta = {}) {
  try {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const body = JSON.stringify({
      page,
      sessionId: getSessionId(),
      visitorId: getVisitorId(),
      isLanding: meta.isLanding || false,
      referrer: meta.referrer || document.referrer || null
    });
    fetch(`${getApiBase()}/analytics/track/pageview`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Sending the token (when present) lets the backend attribute this
        // page view to the logged-in user for engagement analytics.
        ...(token ? { 'x-auth-token': token } : {})
      },
      body,
      keepalive: true
    }).catch(() => {});
  } catch {
    // Never throw
  }
}

/**
 * Track a custom event.
 * @param {string} eventName  e.g. 'clicked_pricing_cta'
 * @param {string} page       current page
 * @param {object} metadata   optional extra data
 */
export function trackEvent(eventName, page, metadata = {}) {
  try {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const body = JSON.stringify({
      eventName,
      page,
      sessionId: getSessionId(),
      metadata
    });
    fetch(`${getApiBase()}/analytics/track/event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'x-auth-token': token } : {})
      },
      body,
      keepalive: true
    }).catch(() => {});
  } catch {
    // Never throw
  }
}
